import { useTranslation } from '@silospot/i18n/react';
import { toast } from '@silospot/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Copy, ShieldCheck, TriangleAlert } from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { usePeriodRange } from '../../stores/period-store';
import { Badge, Btn, Card, CardHead } from './kit';

/**
 * Range/pool ad-click fraud detector + the paste-ready Google Ads exclusion list and
 * the "replace your list" consolidation tool. Lives on the Action Center (detection →
 * action). Visites & IP only carries a sober one-line summary that links here.
 */
export function PoolDetector({ projectId }: { projectId: string }): ReactElement {
  const { t } = useTranslation('pages');
  const range = usePeriodRange();
  const [pasteText, setPasteText] = useState('');

  const rangeQ = useQuery({
    queryKey: ['cld-range-threats', projectId, range],
    queryFn: () => api.getRangeThreats(projectId, range),
    enabled: !!projectId,
  });
  const rangeData = rangeQ.data;

  const copyList = useMemo(() => {
    const items = [
      ...(rangeData?.ranges.map((r) => r.suggestion) ?? []),
      ...(rangeData?.isolatedIps.map((i) => i.suggestion) ?? []),
    ];
    return [...new Set(items)].slice(0, 500);
  }, [rangeData]);
  const copyRanges = () => {
    void navigator.clipboard.writeText(copyList.join('\n'));
    toast.success(t('range.copied'));
  };

  const consolidateM = useMutation({
    mutationFn: () => api.consolidateRanges(projectId, pasteText),
    onError: () => toast.error(t('range.consolidateError')),
  });
  const cons = consolidateM.data;
  const consGroups = cons
    ? {
        detected: cons.entries.filter((e) => e.kind === 'detected').length,
        rolled: cons.entries.filter((e) => e.kind === 'rolled').length,
        protected: cons.protectedCount,
        kept: cons.entries.filter((e) => e.kind.startsWith('kept_')).length,
      }
    : null;
  const copyConsolidated = () => {
    if (!cons) return;
    void navigator.clipboard.writeText(cons.copyList.join('\n'));
    toast.success(t('range.copied'));
  };

  if (!rangeData) {
    return <div className="mb-4 h-24 animate-pulse rounded-2xl bg-neutral-bg" />;
  }

  if (rangeData.ranges.length === 0) {
    return (
      <Card className="mb-4 border-clean-border bg-clean-bg">
        <div className="flex items-start gap-2.5">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-clean-fg" />
          <div>
            <div className="text-[14px] font-bold text-text">{t('range.none')}</div>
            <div className="mt-0.5 text-[12.5px] text-text-2">{t('range.noneSub')}</div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-4 border-[#f3c9c9] bg-fraud-bg">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-fraud-fg" />
            <div>
              <div className="text-[15px] font-bold text-text">
                {rangeData.summary.rangesFlagged} {t('range.title')}
              </div>
              <div className="text-[12.5px] text-text-2">
                {rangeData.summary.adsClicksFlagged} {t('range.adClicks')}
                {rangeData.summary.withForms > 0
                  ? ` · ${rangeData.summary.withForms} ${t('range.withForm')}`
                  : ''}
              </div>
            </div>
          </div>
          <Btn icon={Copy} onClick={copyRanges} disabled={copyList.length === 0}>
            {t('range.copy')} ({copyList.length})
          </Btn>
        </div>

        <div className="mt-2.5 flex items-start gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-[12px] leading-snug text-text-3">
          <span>ℹ</span>
          <span>{t('range.doctrine')}</span>
        </div>

        <div className="mt-3 max-h-[336px] space-y-2 overflow-y-auto pr-1">
          {rangeData.ranges.map((r) => (
            <div key={r.cidr16} className="rounded-lg border border-border bg-surface px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[13px] font-semibold text-text">{r.cidr16}</span>
                  <span className="text-[12px] text-text-3">{r.operator || '—'}</span>
                  {r.forms > 0 ? (
                    <span className="rounded-md border border-[#f6e2c4] bg-[#fdf4e7] px-1.5 py-0.5 text-[11px] font-semibold text-suspect-fg">
                      ⚠ {r.forms} {t('range.form')} · {t('range.verify')}
                    </span>
                  ) : null}
                </div>
                <span className="rounded-md border border-[#f3c9c9] bg-fraud-bg px-2 py-0.5 font-mono text-[12px] font-semibold text-fraud-fg">
                  {r.suggestion}
                </span>
              </div>
              <div className="mt-1 text-[12px] text-text-2">
                {r.adsClicks} {t('range.clicksFrom')} {r.distinctIps} {t('range.distinctIps')} ·{' '}
                {t('range.medianDur')} {r.medianSec}s · {t('range.score')} {r.avgScore}
              </div>
            </div>
          ))}
        </div>

        {rangeData.isolatedIps.length > 0 ? (
          <div className="mt-3 border-t border-border pt-2.5">
            <div className="mb-1.5 text-[12.5px] font-semibold text-text">
              {t('range.isolatedTitle')}{' '}
              <span className="font-normal text-text-3">({rangeData.isolatedIps.length})</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {rangeData.isolatedIps.map((i) => (
                <span
                  key={i.ip}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1 font-mono text-[12px] text-text-2"
                >
                  {i.ip}
                  <span className="text-text-3">· {i.adsClicks}×</span>
                  {i.forms > 0 ? (
                    <span title={t('range.verify')} className="text-suspect-fg">
                      ⚠
                    </span>
                  ) : null}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </Card>

      <Card className="mb-4">
        <CardHead title={t('range.replaceTitle')} sub={t('range.replaceSub')} />
        {cons && consGroups ? (
          <div>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-lg border border-clean-border bg-clean-bg px-3 py-2.5">
              <span className="font-mono text-[14px] text-text-3">{cons.before}</span>
              <span className="text-text-3">→</span>
              <span className="font-mono text-[20px] font-bold text-clean-fg">{cons.after}</span>
              <span className="text-[13px] text-text-2">{t('range.entries')}</span>
              <span className="text-[12.5px] font-semibold text-clean-fg">
                · {t('range.coverageOk')}
              </span>
              {cons.newDetected > 0 ? (
                <span className="text-[12.5px] text-text-2">
                  · +{cons.newDetected} {t('range.newPools')}
                </span>
              ) : null}
            </div>
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <Badge tone="fraud">
                {consGroups.detected} {t('range.grpDetected')}
              </Badge>
              <Badge tone="suspect">
                {consGroups.rolled} {t('range.grpRolled')}
              </Badge>
              <Badge tone="neutral">
                {consGroups.kept} {t('range.grpKept')}
              </Badge>
              {consGroups.protected > 0 ? (
                <Badge tone="blue">
                  {consGroups.protected} {t('range.grpProtected')}
                </Badge>
              ) : null}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Btn primary icon={Copy} onClick={copyConsolidated}>
                {t('range.copyReplace')} ({cons.copyList.length})
              </Btn>
              <Btn onClick={() => consolidateM.reset()}>{t('range.redo')}</Btn>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder={t('range.pastePlaceholder')}
              className="h-28 w-full resize-y rounded-lg border border-border-strong bg-surface px-3 py-2 font-mono text-[12px] outline-none focus:border-primary"
            />
            <Btn
              primary
              onClick={() => consolidateM.mutate()}
              disabled={!pasteText.trim() || consolidateM.isPending}
            >
              {consolidateM.isPending ? '…' : t('range.consolidate')}
            </Btn>
          </div>
        )}
      </Card>
    </>
  );
}
